/**
 * Nutrient Calculation Utilities
 *
 * Provides utilities for calculating nutrient amounts from fertilizer applications.
 */

interface NutrientComposition {
  nutrient_type: string;
  percentage: number;
}

interface StandardFertiliser {
  rate: number;
  nutrients?: NutrientComposition[];
}

interface NutrientAmountResult {
  perHa: number;
  total: number;
}

/**
 * Calculate nutrient amount (kg/ha and total kg) from fertilizer application
 *
 * This function calculates the weighted nutrient percentage from multiple fertilizers
 * based on their application rates and nutrient compositions.
 *
 * @param standardFertilisers - Array of standard fertilizers with rates and nutrient compositions
 * @param fertilizerAmount - The fertilizer amount (kg/ha) applied to the plot
 * @param nutrientType - The type of nutrient to calculate (e.g., "N", "P", "K", "P2O5", "K2O")
 * @param areaHa - The area in hectares for calculating total amount
 * @returns Object containing perHa (kg/ha) and total (kg) nutrient amounts
 *
 * ```
 */
export const calculateNutrientAmount = (
  standardFertilisers: StandardFertiliser[],
  fertilizerAmount: number,
  nutrientType: string,
  areaHa: number
): NutrientAmountResult => {
  // Validate inputs
  if (!standardFertilisers?.length || !fertilizerAmount) {
    return { perHa: 0, total: 0 };
  }

  // Calculate total standard rate for weighting
  const totalStandardRate = standardFertilisers.reduce(
    (sum, fert) => sum + (Number(fert.rate) || 0),
    0
  );

  if (totalStandardRate === 0) {
    return { perHa: 0, total: 0 };
  }

  // Calculate weighted nutrient percentage from all fertilizers
  let totalNutrientPercentage = 0;

  standardFertilisers.forEach((fert) => {
    // Use nutrients stored in standard_fertilisers (ensure it's always an array)
    const nutrients = fert.nutrients ?? [];
    if (Array.isArray(nutrients) && nutrients.length > 0) {
      const nutrientData = nutrients.find(
        (n) => n.nutrient_type === nutrientType
      );
      if (nutrientData && typeof nutrientData.percentage === "number") {
        // Weight the percentage by the fertilizer's rate
        const fertilizerRatio = Number(fert.rate) / totalStandardRate;
        totalNutrientPercentage += nutrientData.percentage * fertilizerRatio;
      }
    }
  });

  // Calculate nutrient amount per hectare and total
  const nutrientPerHa = (fertilizerAmount * totalNutrientPercentage) / 100;
  const nutrientTotal = nutrientPerHa * areaHa;

  return {
    perHa: nutrientPerHa,
    total: nutrientTotal,
  };
};
